import * as Yup from 'yup';
import { createOrderSchema } from './schema';

// Create OSND-specific schema that extends the base schema but removes quote requirements
export const createOsndOrderSchema = createOrderSchema.shape({
  // Override to ensure isOSND is always true
  isOSND: Yup.boolean().oneOf([true], 'OSND flag must be true').default(true),
  
  // Skip quote validation for OSND orders
  skipQuote: Yup.boolean().oneOf([true], 'Quote must be skipped for OSND orders').default(true),
  
  // Keep all other validations from the base schema
  customerUuid: Yup.string().required('Customer is required'),
  
  orderStops: Yup.array().when('orderType', {
    is: (orderType) =>
      orderType === 'point_to_point' ||
      orderType === 'single_shipper' ||
      orderType === 'single_consignee' ||
      orderType === 'sequence',
    then: Yup.array()
      .of(
        Yup.object().shape({
          shipper: Yup.object()
            .shape({
              name: Yup.string().optional(),
              company: Yup.string().optional(),
              shortCode: Yup.string().optional(),
              customerType: Yup.string().optional(),
              billingOption: Yup.string().optional(),
              requireQuote: Yup.boolean().optional(),
              currency: Yup.string().oneOf(['CAD', 'USD']).optional(),
              isActive: Yup.boolean().optional(),
              addresses: Yup.object()
                .shape({
                  city: Yup.string().optional(),
                  state: Yup.string().optional(),
                  postal: Yup.string().optional(),
                  address: Yup.string().optional(),
                  address2: Yup.string().optional(),
                  country: Yup.string().optional(),
                  latitude: Yup.number().nullable().optional(),
                  longitude: Yup.number().nullable().optional(),
                })
                .optional(),
              notes: Yup.string().optional(),
              openingTime: Yup.string().optional(),
              closingTime: Yup.string().optional(),
              contactPhone: Yup.string().optional(),
              contactEmail: Yup.string()
                .email('Invalid email format')
                .optional(),
              contactExt: Yup.string().optional(),
            })
            .optional()
            .test(
              'name-or-company-required',
              'Shipper company name or company field is required',
              function (value) {
                if (!value) return true;
                return !!(value.name || value.company);
              }
            ),

          consignee: Yup.object()
            .shape({
              name: Yup.string().required('Consignee company name is required'),
              shortCode: Yup.string().optional(),
              customerType: Yup.string().optional(),
              billingOption: Yup.string().optional(),
              requireQuote: Yup.boolean().optional(),
              currency: Yup.string().oneOf(['CAD', 'USD']).optional(),
              isActive: Yup.boolean().optional(),
              addresses: Yup.object()
                .shape({
                  city: Yup.string().required('City is required'),
                  state: Yup.string().required('State is required'),
                  postal: Yup.string().required('ZIP code is required'),
                  address: Yup.string().required('Address is required'),
                  address2: Yup.string().optional(),
                  country: Yup.string().required('Country is required'),
                  latitude: Yup.number()
                    .nullable()
                    .required('latitude is required'),
                  longitude: Yup.number()
                    .nullable()
                    .required('longitude is required'),
                })
                .required('Consignee address information is required'),
              notes: Yup.string().optional(),
              openingTime: Yup.string().optional(),
              closingTime: Yup.string().optional(),
              contactPhone: Yup.string().optional(),
              contactEmail: Yup.string()
                .email('Invalid email format')
                .optional(),
              contactExt: Yup.string().optional(),
            })
            .required('Consignee address is required'),

          serviceUuids: Yup.array()
            .of(Yup.string())
            .min(1, 'Service is required')
            .required('Service is required'),
          
          isAppointmentPickup: Yup.boolean().optional(),
          
          commodities: Yup.array()
            .of(
              Yup.object().shape({
                description: Yup.string().required('Description is required'),
                quantity: Yup.number()
                  .typeError('Quantity must be a number')
                  .required('Quantity is required')
                  .positive('Quantity must be positive')
                  .min(1, 'Quantity must be at least 1'),
                weight: Yup.number()
                  .typeError('Weight must be a number')
                  .required('Weight is required')
                  .positive('Weight must be positive')
                  .min(0.1, 'Weight must be at least 0.1'),
                dim: Yup.number()
                  .typeError('DIM must be a number')
                  .optional()
                  .positive('DIM must be positive'),
                packageType: Yup.string().required('Package type is required'),
                isStackable: Yup.boolean().optional(),
                isHazardous: Yup.boolean().optional(),
                nmfcCode: Yup.string().optional(),
                commodityClass: Yup.string().optional(),
              })
            )
            .min(1, 'At least one commodity is required')
            .required('Commodities are required'),
        })
      )
      .min(1, 'At least one order stop is required')
      .required('Order stops are required'),
    otherwise: Yup.array().optional(),
  }),
  
  // Remove quote-related validations for OSND orders
  // OSND orders don't require quote acceptance or selection
  
  status: Yup.string().oneOf(['Draft', 'Accepted', 'Assigned', 'In Transit', 'Delivered']).default('Draft'),
  
  tagUuids: Yup.array().of(Yup.string()).optional(),
});
